import React, { useState, useEffect, useCallback } from 'react';
import styled from 'styled-components';
import axios from 'axios';
import AdminLayout from '../../components/AdminLayout';

const PageHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 1.5rem;
  flex-wrap: wrap;
  gap: 1rem;
`;

const PageTitle = styled.h1`
  font-size: 1.5rem;
  font-weight: 600;
  color: #333;
  margin: 0;
`;

const Button = styled.button`
  padding: 0.5rem 1rem;
  border: none;
  border-radius: 6px;
  cursor: pointer;
  font-size: 0.875rem;
  font-weight: 500;
  background: ${props => props.primary ? '#007bff' : props.success ? '#28a745' : props.danger ? '#dc3545' : '#6c757d'};
  color: #fff;

  &:disabled {
    background: #c0c4cc;
    cursor: not-allowed;
  }
`;

const Card = styled.div`
  background: #fff;
  border: 1px solid #e9ecef;
  border-radius: 8px;
  padding: 1.25rem;
  margin-bottom: 1.5rem;
`;

const FilterBar = styled.div`
  display: flex;
  gap: 0.75rem;
  flex-wrap: wrap;
  align-items: center;
  margin-bottom: 1rem;
`;

const SearchInput = styled.input`
  padding: 0.5rem 0.75rem;
  border: 1px solid #ced4da;
  border-radius: 6px;
  font-size: 0.875rem;
  min-width: 200px;
`;

const FilterSelect = styled.select`
  padding: 0.5rem 0.75rem;
  border: 1px solid #ced4da;
  border-radius: 6px;
  font-size: 0.875rem;
`;

const Table = styled.table`
  width: 100%;
  border-collapse: collapse;
  font-size: 0.875rem;
`;

const Th = styled.th`
  text-align: left;
  padding: 0.75rem;
  border-bottom: 2px solid #e9ecef;
  color: #495057;
  font-weight: 600;
  white-space: nowrap;
`;

const Td = styled.td`
  padding: 0.75rem;
  border-bottom: 1px solid #f1f3f5;
  color: #333;
  vertical-align: middle;
`;

const StatusBadge = styled.span`
  display: inline-block;
  padding: 0.2rem 0.6rem;
  border-radius: 999px;
  font-size: 0.75rem;
  background: ${props => props.$status === 'approved' ? '#e8f5e9' : props.$status === 'rejected' ? '#fdecea' : props.$status === 'pending' ? '#fff8e1' : '#f1f3f5'};
  color: ${props => props.$status === 'approved' ? '#2e7d32' : props.$status === 'rejected' ? '#c62828' : props.$status === 'pending' ? '#856404' : '#6c757d'};
`;

const EmptyState = styled.div`
  text-align: center;
  padding: 2rem;
  color: #6c757d;
`;

const Pagination = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  gap: 0.75rem;
  margin-top: 1rem;
`;

const ModalOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
`;

const ModalContent = styled.div`
  background: #fff;
  border-radius: 8px;
  padding: 1.5rem;
  width: ${props => props.$wide ? '720px' : '460px'};
  max-width: 92%;
  max-height: 88vh;
  overflow-y: auto;
`;

const ModalTitle = styled.h3`
  margin: 0 0 1rem;
  font-size: 1.125rem;
  color: #333;
`;

const TextArea = styled.textarea`
  width: 100%;
  padding: 0.5rem 0.75rem;
  border: 1px solid #ced4da;
  border-radius: 6px;
  font-size: 0.875rem;
  box-sizing: border-box;
  resize: vertical;
`;

const ModalActions = styled.div`
  display: flex;
  justify-content: flex-end;
  gap: 0.75rem;
  margin-top: 1.25rem;
`;

const authHeaders = () => ({
  headers: { Authorization: `Bearer ${localStorage.getItem('adminToken')}` }
});

const STATUS_TEXT = {
  none: '未认证',
  pending: '审核中',
  approved: '已认证',
  rejected: '已驳回'
};

const EngineerVerificationsPage = () => {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [status, setStatus] = useState('pending');
  const [search, setSearch] = useState('');
  const [keyword, setKeyword] = useState('');
  const [page, setPage] = useState(1);
  const [limit] = useState(20);
  const [totalPages, setTotalPages] = useState(1);
  const [total, setTotal] = useState(0);

  const [rejectTarget, setRejectTarget] = useState(null);
  const [rejectReason, setRejectReason] = useState('');
  const [reviewing, setReviewing] = useState(false);
  const [photo, setPhoto] = useState(null); // { url, title }
  const [message, setMessage] = useState('');
  const [messageType, setMessageType] = useState('');

  const notify = (text, type = 'success') => {
    setMessage(text);
    setMessageType(type);
    setTimeout(() => { setMessage(''); setMessageType(''); }, 3000);
  };

  const fetchList = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.get('/api/admin/engineer-verifications', {
        ...authHeaders(),
        params: { status, search: keyword, page, limit }
      });
      if (res.data.success) {
        setUsers(res.data.data.users || []);
        setTotal(res.data.data.pagination.total || 0);
        setTotalPages(res.data.data.pagination.totalPages || 1);
      }
    } catch (error) {
      console.error('获取认证列表失败:', error);
      notify('获取认证列表失败', 'error');
    } finally {
      setLoading(false);
    }
  }, [status, keyword, page, limit]);

  useEffect(() => {
    fetchList();
  }, [fetchList]);

  const handleSearch = () => {
    setPage(1);
    setKeyword(search.trim());
  };

  const review = async (id, action, reason) => {
    setReviewing(true);
    try {
      const res = await axios.post(`/api/admin/engineer-verifications/${id}/review`,
        { action, reason: reason || '' }, authHeaders());
      if (res.data.success) {
        notify(res.data.message || '审核完成');
        setRejectTarget(null);
        setRejectReason('');
        fetchList();
      }
    } catch (error) {
      notify((error.response && error.response.data && error.response.data.message) || '审核失败', 'error');
    } finally {
      setReviewing(false);
    }
  };

  // 证件照片接口需要鉴权，img 标签带不了请求头，这里取回 blob 再显示
  const viewIdCard = async (id, side) => {
    try {
      const token = localStorage.getItem('adminToken');
      const res = await fetch(`/api/admin/engineer-verifications/${id}/idcard/${side}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (!res.ok) {
        notify('照片不存在或已删除', 'error');
        return;
      }
      const blob = await res.blob();
      setPhoto({
        url: URL.createObjectURL(blob),
        title: side === 'front' ? '身份证人像面' : '身份证国徽面'
      });
    } catch (error) {
      notify('读取照片失败', 'error');
    }
  };

  return (
    <AdminLayout title="认证审核">
    <div>
      <PageHeader>
        <PageTitle>技术工程师认证审核</PageTitle>
        <Button onClick={fetchList}>刷新</Button>
      </PageHeader>

      {message && (
        <div style={{
          padding: '0.75rem 1rem',
          borderRadius: 6,
          marginBottom: '1rem',
          background: messageType === 'error' ? '#fdecea' : '#e8f5e9',
          color: messageType === 'error' ? '#c62828' : '#2e7d32',
          fontSize: '0.875rem'
        }}>
          {message}
        </div>
      )}

      <Card>
        <FilterBar>
          <SearchInput
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            onKeyDown={(e) => { if (e.key === 'Enter') handleSearch(); }}
            placeholder="用户名 / 姓名"
          />
          <Button onClick={handleSearch}>搜索</Button>
          <FilterSelect value={status} onChange={(e) => { setStatus(e.target.value); setPage(1); }}>
            <option value="pending">待审核</option>
            <option value="approved">已通过</option>
            <option value="rejected">已驳回</option>
            <option value="all">全部</option>
          </FilterSelect>
          <span style={{ fontSize: '0.85rem', color: '#6c757d' }}>共 {total} 条</span>
        </FilterBar>

        {loading ? (
          <EmptyState>加载中...</EmptyState>
        ) : users.length === 0 ? (
          <EmptyState>暂无认证申请</EmptyState>
        ) : (
          <div style={{ overflowX: 'auto' }}>
            <Table>
              <thead>
                <tr>
                  <Th>用户</Th>
                  <Th>姓名 / 城市</Th>
                  <Th>年限 / 技能</Th>
                  <Th>证件信息</Th>
                  <Th>证件照片</Th>
                  <Th>提交时间</Th>
                  <Th>状态</Th>
                  <Th>操作</Th>
                </tr>
              </thead>
              <tbody>
                {users.map((u) => (
                  <tr key={u._id}>
                    <Td>
                      <div style={{ fontWeight: 500 }}>{u.nickname || u.username}</div>
                      <div style={{ fontSize: '0.75rem', color: '#6c757d' }}>{u.phone || u.email || ''}</div>
                    </Td>
                    <Td>
                      <div>{u.realName || '-'}</div>
                      <div style={{ fontSize: '0.75rem', color: '#6c757d' }}>{u.city || '-'}</div>
                    </Td>
                    <Td>
                      <div>{u.experienceYears || 0} 年</div>
                      <div style={{ fontSize: '0.75rem', color: '#6c757d' }}>
                        {(u.skills || []).slice(0, 3).join('、') || '-'}
                      </div>
                    </Td>
                    <Td>
                      <div>{u.verification.idCardName || '-'}</div>
                      <div style={{ fontSize: '0.75rem', color: '#6c757d', fontFamily: 'monospace' }}>
                        {u.verification.idCardNumber || '-'}
                      </div>
                      {u.verification.ocrSource && (
                        <div style={{ fontSize: '0.7rem', color: '#6c757d' }}>
                          来源：{u.verification.ocrSource === 'manual' ? '手动填写' : '自动识别'}
                        </div>
                      )}
                    </Td>
                    <Td>
                      <div style={{ display: 'flex', gap: '0.35rem', flexWrap: 'wrap' }}>
                        {u.verification.hasFront && (
                          <Button onClick={() => viewIdCard(u._id, 'front')}>人像面</Button>
                        )}
                        {u.verification.hasBack && (
                          <Button onClick={() => viewIdCard(u._id, 'back')}>国徽面</Button>
                        )}
                        {!u.verification.hasFront && !u.verification.hasBack && '-'}
                      </div>
                    </Td>
                    <Td>
                      {u.verification.submittedAt
                        ? new Date(u.verification.submittedAt).toLocaleString('zh-CN')
                        : '-'}
                    </Td>
                    <Td>
                      <StatusBadge $status={u.verification.status}>
                        {STATUS_TEXT[u.verification.status] || u.verification.status}
                      </StatusBadge>
                      {u.verification.status === 'rejected' && u.verification.rejectReason && (
                        <div style={{ fontSize: '0.72rem', color: '#c62828', marginTop: '0.25rem' }}>
                          {u.verification.rejectReason}
                        </div>
                      )}
                    </Td>
                    <Td>
                      {u.verification.status === 'pending' ? (
                        <div style={{ display: 'flex', gap: '0.5rem' }}>
                          <Button success onClick={() => review(u._id, 'approve')}>通过</Button>
                          <Button danger onClick={() => { setRejectTarget(u); setRejectReason(''); }}>驳回</Button>
                        </div>
                      ) : (
                        <span style={{ fontSize: '0.75rem', color: '#6c757d' }}>
                          {u.verification.reviewer ? `由 ${u.verification.reviewer} 处理` : '-'}
                        </span>
                      )}
                    </Td>
                  </tr>
                ))}
              </tbody>
            </Table>
          </div>
        )}

        {totalPages > 1 && (
          <Pagination>
            <Button disabled={page <= 1} onClick={() => setPage(page - 1)}>上一页</Button>
            <span style={{ fontSize: '0.875rem', color: '#6c757d' }}>第 {page} / {totalPages} 页</span>
            <Button disabled={page >= totalPages} onClick={() => setPage(page + 1)}>下一页</Button>
          </Pagination>
        )}
      </Card>

      {/* 驳回原因 */}
      {rejectTarget && (
        <ModalOverlay onClick={() => setRejectTarget(null)}>
          <ModalContent onClick={(e) => e.stopPropagation()}>
            <ModalTitle>驳回认证 · {rejectTarget.nickname || rejectTarget.username}</ModalTitle>
            <p style={{ fontSize: '0.875rem', color: '#495057', marginBottom: '0.75rem' }}>
              请填写驳回原因，用户可在个人中心看到该原因并重新提交。
            </p>
            <TextArea
              rows={4}
              value={rejectReason}
              onChange={(e) => setRejectReason(e.target.value)}
              placeholder="如：证件照片不清晰，请重新上传"
            />
            <ModalActions>
              <Button onClick={() => setRejectTarget(null)}>取消</Button>
              <Button danger disabled={reviewing || !rejectReason.trim()} onClick={() => review(rejectTarget._id, 'reject', rejectReason)}>
                {reviewing ? '提交中...' : '确认驳回'}
              </Button>
            </ModalActions>
          </ModalContent>
        </ModalOverlay>
      )}

      {/* 证件照片 */}
      {photo && (
        <ModalOverlay onClick={() => setPhoto(null)}>
          <ModalContent $wide onClick={(e) => e.stopPropagation()}>
            <ModalTitle>{photo.title}</ModalTitle>
            <img src={photo.url} alt={photo.title} style={{ width: '100%', borderRadius: 6 }} />
            <ModalActions>
              <Button onClick={() => setPhoto(null)}>关闭</Button>
            </ModalActions>
          </ModalContent>
        </ModalOverlay>
      )}
    </div>
    </AdminLayout>
  );
};

export default EngineerVerificationsPage;
